/**
 * 公共牌区 + 底池（§11.4 / §11.6）。
 *
 * 两个容易写错的地方，在这里一次性钉死：
 *
 * 1. **底池数字必须减掉本街未收的投入**。引擎的 `potTotal` 恒为 Σ committedThisHand
 *    （见 types.ts 的注释），本街已下的注**已经**算在里面了。若直接显示 potTotal，
 *    玩家会看到「底池 300 + 桌面上还摆着 200 筹码」，合计比实际多一倍；
 *    收池动画播完数字还纹丝不动，动画就白做了。
 *    所以显示值 = potTotal − Σ committedThisRound，街末投入归零时数字自然涨上去。
 *
 * 2. 公共牌尺寸是**动态**的：`fitBoardCardWidth` 会按舞台宽度和座位块位置收窄它，
 *    所以这里的 Card 必须吃 layout 给的像素值，而不是 CSS 变量 --board-card-w。
 *
 * `ChipStack` 也放在这里导出：底池与座位下注共用同一堆筹码的画法，
 * 两处各画一遍必然会走形。TableScene 直接 import 它。
 */

import { memo } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import type { CardId, Chips, Pot } from '@poker/shared'

import { fxKey } from '../fx/FxController.js'
import { useFxNode } from '../fx/FxLayer.js'
import { useRollingNumber } from '../fx/rollNumber.js'

import { Card, CardSlot } from './Card.js'
import { formatChips } from './cardFormat.js'
import type { TableLayout } from './seatGeometry.js'
import './table.css'

export interface CommunityBoardProps {
  readonly layout: TableLayout
  readonly board: readonly CardId[]
  /** 已翻开的张数；`shownCount..board.length` 之间的牌正在飞入，仍是背面 */
  readonly shownCount: number
  /** 最佳五张高亮集合；非空时其余牌灰化 */
  readonly bestFive?: ReadonlySet<CardId> | null
  readonly reducedMotion?: boolean
}

function CommunityBoardImpl({
  layout,
  board,
  shownCount,
  bestFive,
  reducedMotion = false,
}: CommunityBoardProps): ReactNode {
  void reducedMotion
  const { cardW, cardH, slots } = layout.board
  return (
    <>
      {slots.map((slot, i) => {
        const card = board[i] ?? null
        const style: CSSProperties = { left: `${slot.cx}px`, top: `${slot.cy}px` }
        return (
          <div className="board-slot stage-anchor" style={style} key={`board-${String(i)}`}>
            {card === null ? (
              <CardSlot width={cardW} height={cardH} />
            ) : (
              <Card
                card={card}
                faceUp={i < shownCount}
                size="board"
                fxName={fxKey.board(i)}
                width={cardW}
                height={cardH}
                dim={bestFive != null && bestFive.size > 0 && !bestFive.has(card)}
                best={bestFive != null && bestFive.has(card)}
              />
            )}
          </div>
        )
      })}
    </>
  )
}

export const CommunityBoard = memo(CommunityBoardImpl)

// ============================================================================
// 筹码堆
// ============================================================================

/** 叠放步距（px）：露出下面每一枚的边。必须与 table.css 的 .chip 高度比例配合。 */
const CHIP_STEP = 4
/** 最多叠 4 枚：再多在 16px 宽的堆上只是一条糊掉的竖条，读不出任何信息 */
const CHIP_MAX = 4

/**
 * 筹码堆：按金额决定**枚数**与**面额色**，纯视觉。
 *
 * 金额永远另有文字标注（tabular-nums），所以这里绝不承担传递信息的职责——
 * 颜色单独编码信息会同时坑掉色觉障碍用户与小屏用户（§11.10）。
 * 阈值取整十整百，是为了「筹码堆随注码长高」这件事在一手牌里肉眼可辨。
 */
export function ChipStack({ amount }: { amount: Chips }): ReactNode {
  const n = Math.max(0, amount)
  const tier = n >= 1000 ? 3 : n >= 100 ? 2 : 1
  const count = Math.min(CHIP_MAX, n >= 500 ? 4 : n >= 100 ? 3 : n >= 20 ? 2 : 1)
  const style = { '--chip-lift': `${(count - 1) * CHIP_STEP}px` } as CSSProperties
  return (
    <span className="chip-stack" data-tier={tier} style={style} aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <i className="chip" key={i} style={{ bottom: `${i * CHIP_STEP}px` }} />
      ))}
    </span>
  )
}

export interface PotDisplayProps {
  readonly layout: TableLayout
  /** 引擎口径的 Σ committedThisHand */
  readonly potTotal: Chips
  /** 本街尚未收进池的投入总额 */
  readonly streetCommitted: Chips
  readonly pots: readonly Pot[]
  readonly reducedMotion?: boolean
}

function PotDisplayImpl({
  layout,
  potTotal,
  streetCommitted,
  pots,
  reducedMotion = false,
}: PotDisplayProps): ReactNode {
  const potRef = useFxNode(fxKey.pot())
  // 见文件头第 1 条：桌面上还摆着的筹码不能在底池里再数一遍
  const collected = Math.max(0, potTotal - streetCommitted)
  const amountRef = useRollingNumber(collected, { format: formatChips, reduced: reducedMotion })
  const style: CSSProperties = { left: `${layout.pot.cx}px`, top: `${layout.pot.cy}px` }
  const side = pots.length > 1 ? pots.slice(1) : []

  // 收池前底池确实是空的（盲注还摆在各自面前），显示「底池 0」是噪音
  if (collected <= 0) return null

  return (
    // 外层 .stage-anchor 只负责定位（那条 translate(-50%,-50%) 是静态的），
    // 注册给动效层的是内层：WAAPI 接管 transform 会把外层的居中变换整条替换掉。
    <div className="stage-anchor pot-anchor" style={style}>
    <div className="pot" ref={potRef} aria-label={`底池 ${formatChips(collected)}`}>
      <span className="pot__main">
        <ChipStack amount={collected} />
        <span className="pot__label">底池</span>
        <span className="num" ref={amountRef}>
          {formatChips(collected)}
        </span>
      </span>
      {side.length > 0 && (
        <span className="pot__side">
          {side.map((p, i) => `边池${String(i + 1)} ${formatChips(p.amount)}`).join(' · ')}
        </span>
      )}
    </div>
    </div>
  )
}

export const PotDisplay = memo(PotDisplayImpl)
